import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { format } from "date-fns";
import {
  AlertTriangle,
  CheckCircle,
  PackageSearch,
  Wrench,
  Ban,
  PackageCheck,
  Search,
  Undo2,
  Lock,
} from "lucide-react";

import {
  useDefectiveReturnsQueue, StoreAllocation, useInspectDefectiveReturn, useDefectiveResolutions, useRtvSources, type DefectiveResolution,
} from "@/features/store-allocations/api";
import { useRtvOptions } from "@/features/vendor-returns/api";
import { LocalVendorBadge } from "@/components/LocalVendorBadge";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { hasPermission } from "@/lib/permissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/store-returns/")({
  component: () => (
    <RouteGuard module="store_indents" permission={["view_defective_returns"]}><DefectiveReturnsPage /></RouteGuard>
  ),
});

function DefectiveReturnsPage() {
  const { data: me } = useCurrentUser();
  const canProcess = hasPermission(me, "process_defective_returns");
  const [searchTerm, setSearchTerm] = useState("");
  const { data: allocations, isLoading } = useDefectiveReturnsQueue();

  const [selectedAllocation, setSelectedAllocation] = useState<StoreAllocation | null>(null);

  const filteredData = (allocations || []).filter((a) => {
    const name = a.item_name || a.asset?.name || a.spare?.name || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-destructive flex items-center gap-2">
            <AlertTriangle className="h-6 w-6" /> Defective Returns
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Inspect and manage items that were returned by users as defective.
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Returned Date</TableHead>
              <TableHead>Item Code</TableHead>
              <TableHead>Item Name</TableHead>
              <TableHead>Returned By</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  <div className="flex justify-center items-center gap-2">
                    <PackageSearch className="h-5 w-5 animate-pulse" /> Loading returns...
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No defective returns pending.
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((a) => {
                const name = a.item_name || a.asset?.name || a.spare?.name || "Unknown";
                const date = a.acknowledged_at ? format(new Date(a.acknowledged_at), "dd MMM yyyy, HH:mm") : "-";
                
                let StatusIcon = AlertTriangle;
                let statusColor = "bg-yellow-500/10 text-yellow-600 border-yellow-200";
                
                if (a.inspection_status === "Sent for Repair") {
                  StatusIcon = Wrench;
                  statusColor = "bg-blue-500/10 text-blue-600 border-blue-200";
                } else if (a.inspection_status === "Scrapped") {
                  StatusIcon = Ban;
                  statusColor = "bg-red-500/10 text-red-600 border-red-200";
                } else if (a.inspection_status === "Return to Vendor") {
                  StatusIcon = Undo2;
                  statusColor = "bg-violet-500/10 text-violet-600 border-violet-200";
                } else if (a.inspection_status === "Restocked") {
                  StatusIcon = PackageCheck;
                  statusColor = "bg-green-500/10 text-green-600 border-green-200";
                }

                return (
                  <TableRow key={a.id}>
                    <TableCell className="whitespace-nowrap">{date}</TableCell>
                    <TableCell className="font-mono text-xs">{a.asset?.asset_code || a.spare?.code || "—"}</TableCell>
                    <TableCell className="font-medium">{name}</TableCell>
                    <TableCell>{a.department?.name || "Store"}</TableCell>
                    <TableCell>{a.qty_allocated}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${statusColor}`}>
                        <StatusIcon className="h-3 w-3" />
                        {a.inspection_status || "Pending Inspection"}
                      </Badge>
                      {a.vendor_return && (
                        <div className="mt-1 text-xs">
                          <Link to="/vendor-returns/$rtvId" params={{ rtvId: a.vendor_return.id }} className="text-primary hover:underline">{a.vendor_return.rtv_number}</Link>
                          <span className="text-muted-foreground"> · {a.vendor_return.status}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedAllocation(a)}
                      >
                        {(a.inspection_status === "Pending Inspection" || !a.inspection_status) && canProcess ? "Inspect" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {selectedAllocation && (
        <InspectDialog
          allocation={selectedAllocation}
          canProcess={canProcess}
          open={!!selectedAllocation}
          onOpenChange={(open) => !open && setSelectedAllocation(null)}
        />
      )}
    </div>
  );
}

const RESOLUTION_LABEL: Record<DefectiveResolution, string> = { repair: "Send to Repair", restock: "Restock", return_to_vendor: "Return to Vendor" };

function InspectDialog({
  allocation,
  canProcess,
  open,
  onOpenChange,
}: {
  allocation: StoreAllocation;
  canProcess: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const inspectMutation = useInspectDefectiveReturn();
  const { data: allowed = [] } = useDefectiveResolutions();      // what the backend currently permits (never Scrap)
  const { data: rtvOpts } = useRtvOptions();
  const [remarks, setRemarks] = useState(allocation.inspection_remarks || "");
  const [mode, setMode] = useState<"choose" | "vendor">("choose");
  const [grnItemId, setGrnItemId] = useState("");
  const [reason, setReason] = useState("");

  const isPending = canProcess && (!allocation.inspection_status || allocation.inspection_status === "Pending Inspection");
  const name = allocation.item_name || allocation.asset?.name || allocation.spare?.name || "Unknown Item";
  const { data: src, isLoading: srcLoading } = useRtvSources(allocation.id, isPending && mode === "vendor");
  const canRtv = !!rtvOpts?.can_process;
  const reasons = rtvOpts?.reasons ?? [];

  const run = async (action: DefectiveResolution) => {
    if (inspectMutation.isPending) return;
    if (!remarks.trim()) return toast.error("Inspection remarks are required.");
    try {
      const res = await inspectMutation.mutateAsync({
        allocationId: allocation.id, action, remarks,
        ...(action === "return_to_vendor" ? { grn_item_id: grnItemId, reason: reason.trim() } : {}),
      });
      if (action === "return_to_vendor" && res?.id) {
        toast.success(`${res.rtv_number} created as a Draft return to vendor`);
        navigate({ to: "/vendor-returns/$rtvId", params: { rtvId: res.id } });
      } else {
        toast.success(`Resolved: ${RESOLUTION_LABEL[action]}.`);
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Failed to resolve this return");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Inspect Defective Return</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-xs">Item Name</Label>
              <div className="font-medium">{name}</div>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Item Type</Label>
              <div className="font-medium capitalize">{allocation.kind}</div>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Returned Qty</Label>
              <div className="font-medium">{allocation.qty_allocated}</div>
            </div>
            <div>
              <Label className="text-muted-foreground text-xs">Department</Label>
              <div className="font-medium">{allocation.department?.name || "-"}</div>
            </div>
          </div>

          <div className="bg-muted/30 p-3 rounded-md border">
            <Label className="text-muted-foreground text-xs">User Return Remarks</Label>
            <p className="text-sm mt-1">{allocation.ack_remarks || "No remarks provided."}</p>
            {allocation.ack_attachments && allocation.ack_attachments.length > 0 && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {allocation.ack_attachments.map((path, i) => (
                  <a
                    key={i}
                    href={path.startsWith("http") || path.startsWith("/") ? path : `/media/${path}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block w-16 h-16 border rounded bg-background overflow-hidden hover:opacity-80 transition-opacity"
                  >
                    <img src={path.startsWith("http") || path.startsWith("/") ? path : `/media/${path}`} alt="Attachment" className="object-cover w-full h-full" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Inspection Remarks{isPending ? " *" : ""}</Label>
            {isPending ? (
              <Textarea placeholder="Enter findings after inspecting the item..." value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} />
            ) : (
              <div className="text-sm p-3 bg-muted/30 rounded border">{allocation.inspection_remarks || "No remarks provided."}</div>
            )}
          </div>

          {!isPending && allocation.vendor_return && (
            <div className="text-sm">Return to vendor: <Link to="/vendor-returns/$rtvId" params={{ rtvId: allocation.vendor_return.id }} className="text-primary hover:underline">{allocation.vendor_return.rtv_number}</Link>
              {" "}({allocation.vendor_return.status}) · {allocation.vendor_return.vendor_name}</div>
          )}
          {!isPending && allocation.inspection_status === "Scrapped" && (
            <p className="text-xs text-muted-foreground">Historical record. Scrap is no longer an available resolution for department returns.</p>
          )}

          {isPending && mode === "vendor" && (
            <div className="space-y-3 rounded-md border p-3">
              {!canRtv && (
                <Alert variant="destructive"><Lock className="h-4 w-4" /><AlertTitle>Permission needed</AlertTitle>
                  <AlertDescription>Returning goods to a vendor needs the &quot;Process Vendor Returns (RTV)&quot; permission. An administrator can assign it under Admin → Roles → (role) → Permissions.</AlertDescription></Alert>
              )}
              {srcLoading && <p className="text-sm text-muted-foreground">Checking purchase traceability…</p>}
              {src && !src.traceable && (
                <Alert><AlertTitle>Supplier not traceable</AlertTitle>
                  <AlertDescription>{src.note ?? "No receipt proves where this item was bought."} It stays Pending Inspection; you can still repair or restock it. No vendor is assumed.</AlertDescription></Alert>
              )}
              {src?.traceable && (
                <>
                  <Label className="text-xs">Purchase receipt proving the supplier *</Label>
                  <div className="space-y-1">
                    {src.sources.map((x) => (
                      <label key={x.grn_item_id} className="flex cursor-pointer items-start gap-2 rounded border p-2 text-sm">
                        <input type="radio" name="grn-src" className="mt-1" checked={grnItemId === x.grn_item_id} onChange={() => setGrnItemId(x.grn_item_id)} />
                        <span>{x.vendor_name}{x.vendor_is_one_time && <LocalVendorBadge />}
                          <span className="block text-xs text-muted-foreground">GRN {x.grn_number}{x.po_number ? ` · PO ${x.po_number}` : ""} · received {Number(x.received_qty)}{x.received_date ? ` on ${x.received_date}` : ""}</span></span>
                      </label>
                    ))}
                  </div>
                  <Label className="text-xs">Return reason *</Label>
                  {reasons.length > 0 ? (
                    <select className="w-full rounded border bg-background p-2 text-sm" value={reason} onChange={(e) => setReason(e.target.value)}>
                      <option value="">Select reason</option>
                      {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ) : <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for return to vendor" />}
                  <p className="text-xs text-muted-foreground">This creates a Draft return. Nothing leaves stock until it is dispatched on the Return to Vendor screen.</p>
                  <Button type="button" disabled={inspectMutation.isPending || !canRtv || !grnItemId || !reason.trim() || !remarks.trim()} onClick={() => run("return_to_vendor")}>
                    <Undo2 className="w-4 h-4 mr-2" /> Create Return to Vendor (Draft)
                  </Button>
                </>
              )}
              <Button type="button" variant="ghost" size="sm" onClick={() => setMode("choose")}>Back</Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {isPending && mode === "choose" ? (
            <>
              {allocation.kind === "asset" && allowed.includes("repair") && (
                <Button
                  type="button"
                  variant="outline"
                  className="bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 mr-auto"
                  onClick={() => run("repair")}
                  disabled={inspectMutation.isPending}
                >
                  <Wrench className="w-4 h-4 mr-2" /> Send to Repair
                </Button>
              )}
              {allowed.includes("return_to_vendor") && (
                <Button type="button" variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100" onClick={() => setMode("vendor")} disabled={inspectMutation.isPending}>
                  <Undo2 className="w-4 h-4 mr-2" /> Return to Vendor
                </Button>
              )}
              {allowed.includes("restock") && (
                <Button
                  type="button"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => run("restock")}
                  disabled={inspectMutation.isPending}
                >
                  <PackageCheck className="w-4 h-4 mr-2" /> Restock
                </Button>
              )}
            </>
          ) : (
            <Button type="button" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
